import {
  Box,
  Button,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MouseEvent, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Browser } from '@capacitor/browser';
import { makeStyles } from 'tss-react/mui';
import { useMapLayersState } from 'state/useMapLayersState';
import { MapLayers } from 'features/mapping/mapping.constants';
import { isPhone } from 'shared/envUtils';
import { Helmet } from 'react-helmet';
import { useColorScheme } from 'hooks/useColorScheme';
import { useShareOrCopy } from 'hooks/useShareOrCopy';
import { getCurrentLocationAsCameraShareUrl } from 'shared/utils';
import { useAlertCameraPlayerState } from 'features/cameras/hooks/useAlertCameraPlayerState';
import { getDateFormatted } from 'shared/dates';
import {
  useAlertCamera,
  useAlertCameras,
} from 'features/cameras/hooks/useAlertCameras';
import { AlertCamera } from 'features/cameras/cameras.types';
import { MapEntityDetailsLayout } from 'features/mapping/components/MapEntityDetailsLayout';
import { InlineStatus } from 'components/InlineStatus';
import { useCameraCommandPaletteActions } from 'hooks/commandPaletteActions';
import {
  isTimelapseSupported,
  getCameraAttributionImage,
  getRelatedCameras,
  getCameraAttributionLabelTransKey,
} from 'features/cameras/cameras.utils';
import {
  CAMERA_ATTRIBUTION_LOGO_URL_DARK,
  CAMERA_ATTRIBUTION_LOGO_URL_LIGHT,
} from 'features/cameras/cameras.constants';
import { CameraAttributionLogo } from '../CameraAttributionLogo';
import { CameraPlayer } from '../CameraPlayer';
import { CameraFullScreenButton } from '../CameraFullScreenButton';
import { CameraPreview } from '../CameraPreview';
import { OfflineMessage } from '../OfflineMessage';

const useStyles = makeStyles<{ isTablet: boolean }>()((theme, { isTablet }) => {
  const columnsCount = isTablet ? 4 : 3;
  return {
    cameraGrid: {
      display: 'grid',
      gridColumnGap: theme.spacing(2),
      gridRowGap: theme.spacing(2),
      gridTemplateColumns: `repeat(${columnsCount}, 1fr)`,
    },
  };
});

export const CameraDrawerDetails = (): JSX.Element => {
  const { cameraId } = useParams<{ cameraId: string }>();
  const { t } = useTranslation();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.up('tablet'));
  const { classes } = useStyles({ isTablet });
  const { mapLayers, setMapLayerVisibility } = useMapLayersState();
  const { canShareOrCopy, shareOrCopyUrl } = useShareOrCopy();
  const { isDarkMode } = useColorScheme();
  const { timelapseFrame } = useAlertCameraPlayerState();

  const isCamerasLayerEnabled = mapLayers.includes(MapLayers.CAMERAS);

  // Enable the cameras layer if not on already so the user sees the camera icon/viewshed on the map
  useEffect(() => {
    if (!isCamerasLayerEnabled) {
      setMapLayerVisibility(MapLayers.CAMERAS, true);
    }
  }, [isCamerasLayerEnabled, setMapLayerVisibility]);

  const {
    alertCamera,
    isLoading: isLoadingInstance,
    error: instanceError,
  } = useAlertCamera({ id: cameraId, refetchInterval: 1000 * 15 });

  const {
    alertCameras: allCameras,
    isLoading: isLoadingAll,
    error: allErrors,
  } = useAlertCameras({ enabled: true });

  const relatedCameras = getRelatedCameras(t, alertCamera, allCameras);

  useCameraCommandPaletteActions(cameraId, relatedCameras);

  if (
    isLoadingInstance ||
    isLoadingAll ||
    instanceError ||
    allErrors ||
    !alertCamera ||
    !allCameras
  ) {
    return (
      <InlineStatus
        status={isLoadingInstance || isLoadingAll ? 'loading' : 'error'}
        // This is needed so that the loader is visible when the camera drawer is open at the minimum snap point
        sx={{ minHeight: '15vh' }}
      />
    );
  }

  const attributionToLogoUrl = isDarkMode
    ? CAMERA_ATTRIBUTION_LOGO_URL_DARK
    : CAMERA_ATTRIBUTION_LOGO_URL_LIGHT;

  const attributionImage = getCameraAttributionImage(
    alertCamera,
    attributionToLogoUrl,
  );

  const handleClickAttribution = async (
    event: MouseEvent<HTMLAnchorElement>,
  ): Promise<void> => {
    event.preventDefault();

    await Browser.open({
      url: alertCamera.cameraUrl,
      presentationStyle: 'popover',
    });
  };

  const handleShare = (): void => {
    const formattedDate = getDateFormatted(
      alertCamera.imageTimestamp.toISOString(),
      { includeYear: true, includeTimeZone: true },
    );
    const shareText = `${alertCamera.name} @ ${formattedDate}`;
    shareOrCopyUrl({
      title: alertCamera.name,
      text: shareText,
      url: getCurrentLocationAsCameraShareUrl(
        timelapseFrame ? timelapseFrame.imageUrl : alertCamera.imageUrl,
      ),
    });
  };

  return (
    <>
      <Helmet>
        <title>{t('home.alertCameraTitle', { name: alertCamera.name })}</title>
      </Helmet>
      <MapEntityDetailsLayout.Root>
        <MapEntityDetailsLayout.Header
          title={alertCamera.name}
          sticky={false}
        />

        <MapEntityDetailsLayout.Body>
          {alertCamera.isOffline ? (
            <OfflineMessage camera={alertCamera} />
          ) : (
            <Stack spacing={1}>
              {isTimelapseSupported(alertCamera) ? (
                <CameraPlayer
                  // key prop needed to force component to remount when cameraId
                  // changes so the component's local state is reset; useful when
                  // switching between related cameras
                  key={cameraId}
                  camera={alertCamera}
                />
              ) : (
                <CameraPreview url={alertCamera.imageUrl}>
                  {/* Fullscreen on mobile is exclusively a camera player, so if the camera 
                  doesn't support timelapse, we don't render the fullscreen button. */}
                  {!isPhone() && (
                    <Box
                      sx={{
                        alignSelf: 'flex-end',
                      }}
                    >
                      <CameraFullScreenButton cameraId={cameraId} />
                    </Box>
                  )}
                </CameraPreview>
              )}
            </Stack>
          )}
          {relatedCameras.length > 1 && (
            <>
              <Typography
                id="additional-cameras-header"
                variant="body1"
                component="h4"
                sx={{
                  fontWeight: 'bold',
                  marginBottom: -1,
                }}
              >
                {t('alertCameras.additionalCameras')}
              </Typography>
              <ToggleButtonGroup
                value={alertCamera.id}
                variant="watchduty-gap"
                aria-labelledby="additional-cameras-header"
                className={classes.cameraGrid}
              >
                {relatedCameras?.map((camera: AlertCamera) => (
                  <ToggleButton
                    color="primary"
                    key={camera.id}
                    value={camera.id}
                    size="large"
                    component={Link}
                    to={`/camera/${camera.id}`}
                  >
                    {camera.name}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </>
          )}

          {canShareOrCopy && (
            <MapEntityDetailsLayout.Actions>
              <Button
                fullWidth
                color="neutral"
                variant="outlined"
                size="large"
                onClick={handleShare}
                sx={{ mt: 1 }}
              >
                {t('common.share')}
              </Button>
            </MapEntityDetailsLayout.Actions>
          )}

          {attributionImage && (
            <>
              <Typography
                variant="subtitle1"
                color="textSecondary"
                sx={{
                  textAlign: 'center',
                  textTransform: 'uppercase',
                  fontWeight: 'medium',
                  marginTop: 1,
                }}
              >
                {t(getCameraAttributionLabelTransKey(alertCamera))}
              </Typography>

              <CameraAttributionLogo
                src={attributionImage}
                alt={alertCamera.attribution}
                href={alertCamera.cameraUrl}
                attribution={alertCamera.attribution}
                onClick={handleClickAttribution}
              />
            </>
          )}
        </MapEntityDetailsLayout.Body>
      </MapEntityDetailsLayout.Root>
    </>
  );
};
